import { pipe, map, uniq, filter, propEq, dropLast, flatten, pick, all, propOr, prop } from 'ramda';
import React, { ChangeEvent } from 'react';
import { useRouter } from 'next/router';

import { ArtworkVariant, VariantOptions } from '@definitions/artworks.types';
import { useSetArtworkDynamicData } from '@hooks/useArtworkDynamicData';
import { pickDynamicData } from '@modules/plp/plp.helpers';
import { ROUTE } from '@utils/constants';
import { SelectArrow } from '@components/form';

import { Container, VariantLabel, VariantSelect, VariantOption, LabelCopy } from './artworkVariants.styles';

export interface ArtworkVariantsProps {
  variants: ArtworkVariant[];
  variant: ArtworkVariant;
  artworkSlug: string;
  artistSlug: string;
  setVariant: (d: ArtworkVariant) => void;
}

interface OptionValues {
  [key: string]: string;
}

export const ArtworkVariants = ({ variants, setVariant, variant, artworkSlug, artistSlug }: ArtworkVariantsProps) => {
  const getDisplayNames = (variant: ArtworkVariant) =>
    variant.optionValues.map(({ optionDisplayName }) => optionDisplayName);
  const selectedLabels = pipe(map(getDisplayNames), flatten, uniq)(variants);
  const getSelectedVariantValues: (variant: ArtworkVariant) => { [key: string]: string } = ({ optionValues }) =>
    optionValues.reduce((prev, next) => ({ ...prev, [next.optionDisplayName]: next.label }), {});

  const { setArtworkDynamicData } = useSetArtworkDynamicData();
  const selectedValues = getSelectedVariantValues(variant);
  const router = useRouter();

  const filterVariants = (filterValues: OptionValues) =>
    filter(
      pipe(
        propOr([], 'optionValues'),
        all(({ optionDisplayName, label }: VariantOptions) => {
          return filterValues[optionDisplayName] ? filterValues[optionDisplayName] === label : true;
        })
      )
    ) as (variant: ArtworkVariant[]) => ArtworkVariant[];

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>, index: number) => {
    const { name, value } = e.currentTarget;
    const filterLabels = dropLast(selectedLabels.length - index, selectedLabels);
    const filterValues = pick(filterLabels, selectedValues);
    const selectedVariant =
      filterVariants({ ...selectedValues, [name]: value })(variants)[0] ||
      filterVariants({ ...filterValues, [name]: value })(variants)[0];

    setVariant(selectedVariant);

    const isExclusive = Boolean(router.query.exclusive);
    const exclusiveQuery = isExclusive ? { exclusive: router.query.exclusive } : {};

    router
      .push(
        {
          pathname: `${ROUTE.ARTWORKS}/${artistSlug}/${artworkSlug}`,
          query: { variantId: selectedVariant.bigCommerceId, ...exclusiveQuery },
        },
        undefined,
        {
          shallow: true,
        }
      )
      .finally(() => ({}));
    setArtworkDynamicData(pickDynamicData(selectedVariant));
  };

  const getValues = (label: string, index: number) => {
    const filterLabels = dropLast(selectedLabels.length - index, selectedLabels);
    const filterValues = pick(filterLabels, selectedValues);
    const filterDisplayNames = (variant: ArtworkVariant) =>
      filter(propEq('optionDisplayName', label), variant.optionValues);

    const filterOptions: (data: ArtworkVariant[]) => string[] = pipe(
      filterVariants(filterValues),
      map(filterDisplayNames),
      flatten,
      map(prop('label')),
      uniq
    );

    return filterOptions(variants);
  };

  return (
    <Container>
      {selectedLabels.map((label, index) => (
        <VariantLabel htmlFor={label} key={index}>
          <LabelCopy>{label}:</LabelCopy>
          <VariantSelect
            data-testid={`variant-select-${index}`}
            name={label}
            id={label}
            onChange={(e) => handleSelectChange(e, index)}
            value={selectedValues[label]}
          >
            {getValues(label, index).map((value, index) => (
              <VariantOption key={index} value={value}>
                {value}
              </VariantOption>
            ))}
          </VariantSelect>
          <SelectArrow />
        </VariantLabel>
      ))}
    </Container>
  );
};
